<template>
    <div class="relative contents">
        <input
            :id="id"
            ref="input"
            v-model="modelValue"
            :class="['forms__input', $attrs.class, sizeClass, { 'forms__input--disabled': props.disabled }]"
            :placeholder="placeholder"
            :type="props.type"
            :dusk="dusk"
            :autocomplete="autocomplete"
            :required="required"
            :disabled="disabled"
            :min="props.min"
            :max="props.max"
            :tabindex="props.tabindex"
            :step="props.step"
            :readonly="props.readonly"
            v-bind="$attrs"
            data-1p-ignore
            @focus="emit('focus', $event)"
            @blur="emit('blur', $event)"
        />

        <div v-if="props.valid" class="absolute text-green-800 -translate-y-1/2 top-1/2 right-4">
            <icon icon="hero/outline/check" class="w-4 h-4"></icon>
        </div>

        <div v-if="props.invalid" class="absolute text-red-800 -translate-y-1/2 top-1/2 right-4">
            <icon icon="hero/outline/exclamation-circle" class="w-4 h-4"></icon>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { Ref } from 'vue';
import Icon from '@src/Common/Components/Icon.vue';
import type { FieldComponent } from '@src/Common/Components/types';

defineOptions({
    inheritAttrs: false,
});

const modelValue = defineModel({
    type: [String, Number],
});

const emit = defineEmits<{
    (event: 'focus', content: any): void;
    (event: 'blur', content: any): void;
}>();

const props = withDefaults(defineProps<FieldComponent>(), {
    placeholder: undefined,
    value: null,
    size: 'base',
    type: 'text',
    dusk: null,
    valid: false,
    invalid: false,
    autocomplete: 'off',
    id: null,
    required: false,
    disabled: false,
    min: null,
    max: null,
    tabindex: null,
    step: null,
    readonly: false,
});

const sizeClass: Ref<string> = computed((): string => {
    const sizes = {
        xs: 'forms__input--xs',
        sm: 'forms__input--sm',
        base: 'forms__input--base',
        lg: 'forms__input--lg',
        xl: 'forms__input--xl',
    };

    return `${sizes[props.size] ?? sizes.base} ${props.valid || props.invalid ? 'pr-8' : ''}`;
});

const input = ref(null as HTMLInputElement | null);

const focus = (): void => {
    if (!props.disabled) {
        input.value?.focus();
    }
};

defineExpose({ focus });

const placeholder = computed((): string => (props.placeholder ? `${props.placeholder}` : ''));
</script>
